import { selectLabels, selectRegions } from '@redux/globalSlice'

import Button from '@components/ui/ButtonV2/Button'
import ShoppingCart from '@components/ui/Icons/ShoppingCart'
import { closeModal } from '@redux/modalSlice'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import clsx from 'clsx'
import { REGIONS } from 'config'
import { useRouter } from 'next/router'
import { match } from 'path-to-regexp'
import {
  CSSProperties,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './MarketSelection.module.scss'

type MarketSelectionProps = {
  setFullScreen?:
    | Dispatch<SetStateAction<'small' | 'expanded' | undefined>>
    | undefined
}

const MarketSelection: FC<MarketSelectionProps> = ({ setFullScreen }) => {
  const router = useRouter()
  const regions = useSelector(selectRegions)

  const dispatch = useDispatch()
  const labels = useSelector(selectLabels)

  const pathPattern = '/:lang/:country/:path*'
  const matchFn = match(pathPattern, { decode: decodeURIComponent })
  const pathWithoutQuery = router.asPath.split('?')[0]
  const relativePath = matchFn(pathWithoutQuery)
  const queryParamsString = router.asPath.split('?')[1] || ''
  const queryParams = new URLSearchParams(queryParamsString)

  const [expanded, setExpanded] = useState<number | null>(null)

  useEffect(() => {
    if (setFullScreen) setFullScreen('expanded')
  }, [])

  const regionListWrapper = document.querySelector(
    `.${styles.regionListWrapper}`,
  )
  const regionList = document.querySelector(`.${styles.regionList}`)

  // handle regionList scroll
  const handleRegionListScroll = () => {
    if (regionListWrapper && regionList) {
      if (regionList.scrollTop > 0) {
        regionListWrapper.classList.add(styles.scroll)
      } else {
        regionListWrapper.classList.remove(styles.scroll)
      }

      // if scroll reaches bottom
      if (
        regionList.scrollTop + regionList.clientHeight + 1 >=
        regionList.scrollHeight
      ) {
        regionListWrapper.classList.remove(styles.overflow)
      } else {
        regionListWrapper.classList.add(styles.overflow)
      }
    }
  }

  const heights = regions.map((region) => {
    const countryList = document.querySelector(
      `[data-idx="${regions.indexOf(region)}"]`,
    )
    if (countryList) {
      return countryList.scrollHeight
    }
    return 0
  })

  useEffect(() => {
    if (regionListWrapper && regionList) {
      setTimeout(() => {
        if (regionList.scrollHeight > regionList.clientHeight) {
          regionListWrapper.classList.add(styles.overflow)
        } else {
          regionListWrapper.classList.remove(styles.overflow)
        }
      }, 0)

      disableBodyScroll(regionList, { reserveScrollBarGap: true })

      regionList.addEventListener('scroll', handleRegionListScroll)

      return () => {
        regionList.removeEventListener('scroll', handleRegionListScroll)
        clearAllBodyScrollLocks()
      }
    }
  }, [regionList, regionListWrapper, expanded])

  return (
    <div className={styles.root}>
      <div>
        <div className="headerTwo pb-2">
          {labels.labelCountrySelectionHeadline}
        </div>
        <div className="bodyText">{labels.labelCountrySelectionBody}</div>
      </div>

      <div className={styles.regionListWrapper}>
        <div className={styles.regionList}>
          {REGIONS.map((region, index) => (
            <div className={styles.countriesWrapper} key={region.name}>
              <div
                className={clsx('headerTwo', styles.regionName, {
                  [styles.expanded]: expanded === index,
                })}
                onClick={() => {
                  if (expanded === index) {
                    setExpanded(null)
                  } else {
                    setExpanded(index)
                  }
                }}
              >
                {labels[`labelRegion${region.name}`]}
                <span className={styles['accordion-chevron']}>
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="17" cy="17" r="17" fill="black" />
                    <rect
                      x="24"
                      y="16.6111"
                      width="1"
                      height="14"
                      transform="rotate(90 24 16.6111)"
                      fill="white"
                    />
                    <rect
                      x="16.6113"
                      y="10"
                      width="1"
                      height="14"
                      fill="white"
                      className={styles['animated-line']}
                    />
                  </svg>
                </span>
              </div>
              <ul
                className={clsx(styles.countryList, {
                  [styles.expanded]: expanded === index,
                })}
                data-idx={index}
                style={{ '--height': heights[index] + 'px' } as CSSProperties}
              >
                {[...region.countries]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((country) => (
                    <a
                      key={country.code}
                      className={clsx(styles.countryItem, 'w-full')}
                      href={`/en/${country.code.toLowerCase()}${
                        relativePath
                          ? (relativePath.params as any)?.path?.join('/')
                            ? `/${(relativePath.params as any)?.path?.join(
                                '/',
                              )}`
                            : ''
                          : ''
                      }${
                        queryParams.toString()
                          ? `?${queryParams.toString()}`
                          : ''
                      }`}
                      onClick={() => {
                        sessionStorage.setItem(
                          'hideMarketMatchingModal',
                          'true',
                        )
                        dispatch(closeModal())
                      }}
                    >
                      <Button
                        key={country.code}
                        className={clsx('w-full text-left !justify-start')}
                        labelClassName="flex w-full gap-4"
                      >
                        <img
                          style={{ width: '18px', height: '24px' }}
                          loading="lazy"
                          src={`https://media.gubi.com/countries/${country?.code.toUpperCase()}.svg`}
                          alt={country.name}
                        />
                        <div className="flex flex-col w-full">
                          <div className="flex items-center w-full justify-between gap-4">
                            <div className="bodyOne">{country?.name}</div>
                            {country.ecommerceEnabled && (
                              <ShoppingCart
                                className={styles.centerIcon}
                                title={'Ecommerce Store'}
                              />
                            )}
                          </div>
                          <div
                            className={
                              'flex items-center w-full justify-between gap-4'
                            }
                          >
                            <div className={'bodyText'}>English</div>
                            {country.ecommerceEnabled &&
                              country.currencies.map((currency) => (
                                <div
                                  className={'bodyText'}
                                  id={currency}
                                  key={currency}
                                >
                                  {currency}
                                </div>
                              ))}
                          </div>
                        </div>
                      </Button>
                    </a>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MarketSelection
